import { create } from 'zustand'

import { emitirBilhete, subprotocolosDaSessao, urlDaSessao } from '@/api/ssm'
import {
  SessaoDeTerminal,
  type LeituraDeFechamento,
  type SocketMinimo,
} from '@/pages/ssm/protocolo'

import { useAuthStore } from './auth'

/**
 * Dono das sessões de shell (TASK-10345).
 *
 * ## Por que saiu da página
 *
 * Até 04/09/2026 as sessões viviam em `refs` do `useSessoesSsm`, dentro da tela de
 * Servidores, e um `useEffect` de cleanup encerrava todas ao desmontar. Enquanto for
 * assim, terminal que sobrevive a sair da tela é impossível.
 *
 * Store de módulo sobrevive a troca de rota por construção — não precisa de provider
 * novo no `AppLayout`, e não depende de nenhum componente estar montado.
 *
 * ## O que NÃO entra no estado reativo
 *
 * O `SessaoDeTerminal`, o socket e o buffer de saída. São objeto mutável, não
 * serializável, e cada byte que chega do servidor não pode disparar render: um
 * `tail -f` movimentado repintaria a árvore a cada linha. Eles ficam nos mapas de
 * módulo abaixo; o store guarda só o que a interface desenha.
 *
 * É a mesma separação que o hook já fazia com `refs` — o que muda é o escopo, de
 * componente para módulo.
 *
 * ## O fusível que saiu daqui, e para onde foi
 *
 * O cleanup que encerrava tudo ao desmontar a tela era, na prática, o único limite
 * de uma sessão esquecida. Ele foi substituído por dois, e os dois são do broker:
 * ocioso de 30 min e duração de 8h (TASK-10343), mais o teto de gravação
 * (TASK-10348). Aqui ficou só o que é do cliente: o fim da sessão do CONSOLE
 * encerra as sessões de shell (ver o final deste arquivo).
 */

export type EstadoDaAba = 'conectando' | 'aberta' | 'encerrada' | 'recusada'

export type ModoDeExibicao = 'aba' | 'flutuante'

export interface Geometria {
  x: number
  y: number
  w: number
  h: number
}

export interface SessaoNaTela {
  instanceId: string
  nome: string
  ambiente: string
  tenantId: number
  /** O título da janela precisa dizer de qual CLIENTE é aquele shell. */
  tenantNome: string
  estado: EstadoDaAba
  /** Preenchido quando a sessão fecha — diz o que fazer, não só o que houve. */
  motivo: LeituraDeFechamento | null
  modo: ModoDeExibicao
  /** Só quando `modo === 'flutuante'`. Em memória: um F5 mata a sessão junto. */
  geometria: Geometria | null
}

interface EstadoSsm {
  sessoes: Record<string, SessaoNaTela>
  ativa: string | null
  historico: (instanceId: string) => Uint8Array[]
}

// ── fora do estado reativo ──────────────────────────────────────────────────

const vivas = new Map<string, SessaoDeTerminal>()
const buffers = new Map<string, Uint8Array[]>()
const ouvintes = new Map<string, Set<(d: Uint8Array) => void>>()
/** Reconexões agendadas depois de um 4012 — ver `agendarReconexao`. */
const reconexoes = new Map<string, ReturnType<typeof setTimeout>>()
/**
 * O último tamanho que o terminal da tela mediu, por instância.
 *
 * O terminal mede ao montar — antes de existir sessão (o bilhete ainda vem) — e
 * só mede de novo quando a janela muda. Toda sessão NOVA sobre a mesma tela (a
 * primeira, a reconexão do 4012, o "Abrir de novo") recebe este tamanho ao ser
 * criada; sem isso, o PTY remoto nascia no 80x24 padrão e ficava nele. Não sai
 * no `encerrarSessao`: o "Abrir de novo" encerra e reabre sem remontar o terminal.
 */
const tamanhos = new Map<string, { cols: number; lines: number }>()

function entregar(instanceId: string, dados: Uint8Array): void {
  buffers.get(instanceId)?.push(dados)
  ouvintes.get(instanceId)?.forEach(fn => fn(dados))
}

function linha(instanceId: string, cor: number, texto: string): void {
  entregar(instanceId, new TextEncoder().encode(`\r\n\x1b[${cor}m${texto}\x1b[0m\r\n`))
}

/** Costuras que só o teste usa. Não chame daqui de dentro da aplicação. */
export const _internals = { vivas, buffers, ouvintes, reconexoes, tamanhos, entregar }

/**
 * Esperas entre as tentativas de reconectar depois de um 4012 (consoleai#26055).
 *
 * O 4012 chega quando a task do broker sai num deploy, e a tentativa cai numa
 * task nova — ou, nos primeiros segundos do rollout, noutra que também está
 * saindo, que devolve 4012 de novo. Cinco tentativas, ~30 s ao todo, cobrem o
 * rollout do broker sem martelar a plataforma; esgotadas, a janela volta ao
 * "Abrir de novo" de sempre.
 */
export const ESPERAS_DA_RECONEXAO_MS = [1_000, 2_000, 4_000, 8_000, 15_000] as const

export const useSsmStore = create<EstadoSsm>(() => ({
  sessoes: {},
  ativa: null,
  historico: (instanceId: string) => buffers.get(instanceId) ?? [],
}))

function mexer(instanceId: string, mudanca: Partial<SessaoNaTela>): void {
  useSsmStore.setState(estado => {
    const atual = estado.sessoes[instanceId]
    if (!atual) return estado
    return { sessoes: { ...estado.sessoes, [instanceId]: { ...atual, ...mudanca } } }
  })
}

// ── ciclo de vida ───────────────────────────────────────────────────────────

/**
 * Abre uma sessão. ASSÍNCRONO: o bilhete de uso único vem ANTES do socket.
 *
 * Um WebSocket de navegador não aceita cabeçalho, então a credencial precisa estar
 * em mãos para viajar no `Sec-WebSocket-Protocol` do handshake. Falhar aqui é
 * RECUSA, não queda — a aba precisa dizer isso, porque a ação é outra.
 */
export async function abrirSessao(dados: {
  instanceId: string
  nome: string
  ambiente: string
  tenantId: number
  tenantNome: string
}): Promise<void> {
  const { instanceId } = dados
  if (vivas.has(instanceId) || reconexoes.has(instanceId)) {
    useSsmStore.setState({ ativa: instanceId })
    return
  }

  useSsmStore.setState(estado => ({
    ativa: instanceId,
    sessoes: {
      ...estado.sessoes,
      [instanceId]: {
        ...dados,
        estado: 'conectando',
        motivo: null,
        modo: 'aba',
        geometria: null,
      },
    },
  }))
  buffers.set(instanceId, [])

  await conectar(instanceId, 0)
}

/**
 * Bilhete + socket para uma sessão que já está no store.
 *
 * `tentativa` > 0 é RECONEXÃO depois de um 4012: o buffer, o modo e a geometria
 * da janela ficam, e o que muda é só o socket — com uma sessão SSM NOVA do
 * outro lado.
 */
async function conectar(instanceId: string, tentativa: number): Promise<void> {
  const tenantId = useSsmStore.getState().sessoes[instanceId]?.tenantId
  if (tenantId === undefined) return

  let bilhete: string
  try {
    bilhete = await emitirBilhete(tenantId)
  } catch {
    // Na reconexão, falha de bilhete é mais uma tentativa que não deu — a api
    // pode estar no mesmo rollout. Na abertura, é recusa, como sempre foi.
    if (tentativa > 0 && agendarReconexao(instanceId, tentativa)) return
    mexer(instanceId, {
      estado: 'recusada',
      motivo: {
        mensagem:
          'A plataforma não autorizou esta sessão: a credencial de acesso ao '
          + 'servidor não foi emitida. Tentar de novo costuma resolver. Se '
          + 'acontecer sempre, o que falta é a permissão de shell neste ambiente '
          + '— peça ao time de plataforma.',
        vaiAdiantarTentarDeNovo: true,
        exigeLoginNovo: false,
      },
    })
    return
  }
  // Fechada enquanto o bilhete vinha: abrir agora seria um shell sem janela.
  if (!useSsmStore.getState().sessoes[instanceId]) return

  const socket = new WebSocket(
    urlDaSessao(tenantId, instanceId),
    subprotocolosDaSessao(bilhete),
  ) as unknown as SocketMinimo

  // Quantas reconexões seguidas já houve. Zera quando o socket ABRE: o 4012 do
  // deploy de amanhã ganha as cinco tentativas inteiras de novo.
  let seguidas = tentativa

  const sessao = new SessaoDeTerminal(
    socket,
    {
      escrever: d => entregar(instanceId, d),
      aviso: texto => linha(instanceId, 33, texto),
    },
    leitura => {
      vivas.delete(instanceId)
      if (leitura.reconectaSozinho && agendarReconexao(instanceId, seguidas)) return
      // `recusada` e `encerrada` são estados diferentes: um pede ação da pessoa
      // (pedir acesso, fechar outra aba), o outro é só o fim normal. Colapsá-los
      // faria a lista mentir sobre o que houve.
      mexer(instanceId, {
        estado:
          leitura.exigeLoginNovo || !leitura.vaiAdiantarTentarDeNovo
            ? 'recusada'
            : 'encerrada',
        motivo: leitura,
      })
    },
    // Quarto argumento, e não `socket.onopen = ...`: atribuir ali sobrescreve o
    // handler que a própria `SessaoDeTerminal` instala para marcar `aberto`. Sem
    // esse estado, `digitar()` vira no-op silencioso — o !1413.
    () => {
      if (seguidas > 0) {
        // Dito com todas as letras: é OUTRO shell. Quem tinha um `top` ou um
        // `tail -f` rodando precisa saber que ele parou, e não esperar que volte.
        linha(
          instanceId,
          36,
          'Sessão nova aberta. O shell anterior foi encerrado pela atualização '
          + 'da plataforma — o que rodava nele parou.',
        )
      }
      seguidas = 0
      mexer(instanceId, { estado: 'aberta', motivo: null })
    },
  )

  vivas.set(instanceId, sessao)
  const tamanho = tamanhos.get(instanceId)
  if (tamanho) sessao.redimensionar(tamanho.cols, tamanho.lines)   // vai na abertura
}

/**
 * Agenda a próxima tentativa depois de um 4012. Devolve `false` quando as
 * tentativas acabaram — aí quem chamou segue o caminho de sempre.
 */
function agendarReconexao(instanceId: string, feitas: number): boolean {
  if (!useSsmStore.getState().sessoes[instanceId]) return false
  const espera = ESPERAS_DA_RECONEXAO_MS[feitas]
  if (espera === undefined) {
    mexer(instanceId, {
      estado: 'encerrada',
      motivo: {
        mensagem:
          'A plataforma foi atualizada e não consegui reconectar sozinho. Abra '
          + 'de novo: será uma sessão nova.',
        vaiAdiantarTentarDeNovo: true,
        exigeLoginNovo: false,
      },
    })
    linha(instanceId, 33, 'Não consegui reconectar depois da atualização da plataforma.')
    return true
  }
  linha(instanceId, 36, 'Reconectando…')
  mexer(instanceId, { estado: 'conectando', motivo: null })
  reconexoes.set(
    instanceId,
    setTimeout(() => {
      reconexoes.delete(instanceId)
      void conectar(instanceId, feitas + 1)
    }, espera),
  )
  return true
}

export function encerrarSessao(instanceId: string): void {
  clearTimeout(reconexoes.get(instanceId))
  reconexoes.delete(instanceId)
  vivas.get(instanceId)?.encerrar()
  vivas.delete(instanceId)
  buffers.delete(instanceId)
  ouvintes.delete(instanceId)
  useSsmStore.setState(estado => {
    const { [instanceId]: _fora, ...resto } = estado.sessoes
    return {
      sessoes: resto,
      ativa: estado.ativa === instanceId ? null : estado.ativa,
    }
  })
}

export function encerrarTodasAsSessoes(): void {
  // As que esperam reconexão também: sem isto, o logout deixaria um timer
  // reabrindo shell para quem acabou de sair.
  for (const id of new Set([...vivas.keys(), ...reconexoes.keys()])) encerrarSessao(id)
}

// ── o que a interface faz ───────────────────────────────────────────────────

/**
 * Abre de novo, sobre a MESMA instância — e é sessão nova, não reconexão.
 *
 * Com os fusíveis do broker a sessão morre sozinha (inatividade, tempo máximo,
 * gravação cheia), e reabrir pede bilhete novo e abre uma gravação NOVA, com outro
 * `recording_id`. Emendar as duas na trilha seria mentira: são dois shells.
 *
 * O modo e a geometria são preservados, senão a janela pinada voltaria como aba e
 * a pessoa teria de pinar de novo — punição por algo que o servidor decidiu.
 */
export function reabrirSessao(instanceId: string): void {
  const antiga = useSsmStore.getState().sessoes[instanceId]
  if (!antiga) return
  const { modo, geometria, nome, ambiente, tenantId, tenantNome } = antiga

  encerrarSessao(instanceId)
  void abrirSessao({ instanceId, nome, ambiente, tenantId, tenantNome }).then(() => {
    mexer(instanceId, { modo, geometria })
  })
}


export function pinar(instanceId: string): void {
  mexer(instanceId, { modo: 'flutuante' })

  // Sair de `ativa` faz parte de pinar. Sem isto a sessao fica desenhada DUAS
  // vezes — no painel da tela e na janela — e o efeito nao e estetico: cada
  // `TerminalDoServidor` tem o seu `ResizeObserver` e manda o proprio
  // `cols/lines` para o PTY remoto, um largo e outro estreito. Os dois disputam
  // a geometria do terminal do outro lado, e o programa remoto redesenha para a
  // ultima que chegou. Visto na tela em 04/09/2026, com `htop`.
  //
  // O painel passa para a proxima sessao ainda em ABA; nao havendo nenhuma, cai
  // no estado vazio — e a faixa "N pinadas" diz para onde a sessao foi.
  const estado = useSsmStore.getState()
  if (estado.ativa !== instanceId) return
  const proxima = Object.values(estado.sessoes).find(
    s => s.instanceId !== instanceId && s.modo === 'aba',
  )
  useSsmStore.setState({ ativa: proxima?.instanceId ?? null })
}

export function despinar(instanceId: string): void {
  mexer(instanceId, { modo: 'aba' })
}

export function moverJanela(instanceId: string, geometria: Geometria): void {
  mexer(instanceId, { geometria })
}

export function ativar(instanceId: string | null): void {
  useSsmStore.setState({ ativa: instanceId })
}

export function digitar(instanceId: string, texto: string): void {
  vivas.get(instanceId)?.digitar(texto)
}

export function redimensionar(instanceId: string, cols: number, lines: number): void {
  if (cols > 0 && lines > 0) tamanhos.set(instanceId, { cols, lines })
  vivas.get(instanceId)?.redimensionar(cols, lines)
}

export function sessaoViva(instanceId: string): SessaoDeTerminal | undefined {
  return vivas.get(instanceId)
}

/**
 * Registra um ouvinte e devolve a função que o REMOVE.
 *
 * Conjunto, e não um por sessão: com um só, o StrictMode do React 18 monta o
 * terminal duas vezes, o segundo `ouvir` sobrescreve o primeiro, e o unmount do
 * primeiro apaga o ouvinte VIVO. Terminal perfeito e mudo — o !1413 de novo, só
 * que visível apenas em desenvolvimento, onde ninguém liga o sintoma à causa.
 */
export function ouvir(
  instanceId: string,
  fn: (d: Uint8Array) => void,
): () => void {
  const conjunto = ouvintes.get(instanceId) ?? new Set<(d: Uint8Array) => void>()
  conjunto.add(fn)
  ouvintes.set(instanceId, conjunto)
  return () => {
    conjunto.delete(fn)
    if (conjunto.size === 0) ouvintes.delete(instanceId)
  }
}

// ── o fim da sessão do console encerra o shell ──────────────────────────────
//
// INSCRIÇÃO NO STORE, e não `useEffect` de componente. O `AppLayout` mora dentro
// do `<RequireAuth>` (`App.tsx`): quando a autenticação cai, ele JÁ desmontou, e um
// efeito de componente chegaria tarde. Sem isto, sair do console deixaria shell root
// vivo em servidor de cliente, gravando e ocupando vaga, com ZERO interface na tela
// até o fusível de 30 min do broker.
//
// Cobre logout, expiração e qualquer outro caminho que zere `isAuthenticated` —
// inclusive o interceptador de 401, que não passa pelo botão de sair.
useAuthStore.subscribe((estado, anterior) => {
  if (anterior.isAuthenticated && !estado.isAuthenticated) {
    encerrarTodasAsSessoes()
  }
})
